import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Printer, Trash2 } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import InvoiceFormSimple from '../../components/sales/InvoiceFormSimple';
import PaymentModal from '../../components/sales/PaymentModal';
import api from '../../lib/api';

// Helper function to safely extract error message
const getErrorMessage = (error) => {
  if (!error.response) {
    return error.message || 'Network error occurred';
  }

  const { status, data } = error.response;

  if (status === 422) {
    if (data.detail) {
      if (Array.isArray(data.detail)) {
        const firstError = data.detail[0];
        if (typeof firstError === 'object') {
          return `${firstError.loc?.[firstError.loc.length - 1] || 'Field'}: ${firstError.msg || 'Invalid value'}`;
        }
        return String(firstError);
      }
      if (typeof data.detail === 'string') {
        return data.detail;
      }
    }
  }

  if (data.detail) {
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }

  if (data.message) {
    return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
  }

  return `Request failed with status ${status}`;
};

const Invoices = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(storedUser));
    fetchInvoices();
  }, [navigate, statusFilter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/invoices?${params}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (invoice) => {
    if (!confirm(`Confirm invoice ${invoice.invoice_number}? This will deduct stock.`)) return;

    try {
      const response = await api.post(`/invoices/${invoice.id}/confirm`);
      alert(response.data.message || 'Invoice confirmed successfully');
      fetchInvoices();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      alert(`Failed to confirm invoice: ${errorMessage}`);
      console.error('Confirm invoice error:', error);
    }
  };

  const handleCancel = async (invoice) => {
    const msg = `Cancel invoice ${invoice.invoice_number}? Unpaid invoices with complete confirmation evidence will have stock and accounting entries reversed.`;
    if (!confirm(msg)) return;

    try {
      const response = await api.post(`/invoices/${invoice.id}/cancel`);
      alert(response.data.message || 'Invoice cancelled. Stock and accounting entries were reversed exactly.');
      fetchInvoices();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      alert(`Failed to cancel invoice: ${errorMessage}`);
      console.error('Cancel invoice error:', error);
    }
  };

  const handleDelete = async (invoice) => {
    if (invoice.status !== 'draft') {
      alert('Only draft invoices can be deleted. Posted invoice evidence must be retained.');
      return;
    }
    if (!confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}? This cannot be undone.`)) return;

    try {
      const response = await api.delete(`/invoices/${invoice.id}`);
      alert(response.data.message || 'Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      alert(`Failed to delete invoice: ${errorMessage}`);
      console.error('Delete invoice error:', error);
    }
  };

  const handleRecordPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    fetchInvoices();
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchInvoices();
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="invoices-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="invoices-title">
              Sales Invoices
            </h1>
            <button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors flex items-center gap-2"
              data-testid="add-invoice-button"
            >
              <Plus className="w-5 h-5" />
              New Invoice
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">Status Filter</label>
                <select
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="paid">Paid</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No invoices found</p>
              <button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors">
                Create Your First Invoice
              </button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[600px]" data-testid="invoices-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">Invoice #</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Issue Date</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4">Paid</th>
                    <th className="py-3 px-4">Balance</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{invoice.invoice_number}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{invoice.customer?.name || 'N/A'}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{new Date(invoice.issue_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">Rs. {parseFloat(invoice.total_amount).toLocaleString()}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">Rs. {parseFloat(invoice.amount_paid).toLocaleString()}</td>
                      <td className="py-3 px-4 font-semibold text-[#EF4444]">Rs. {parseFloat(invoice.balance_due).toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          invoice.status === 'paid' ? 'bg-[#DBEAFE] text-[#1E40AF]' :
                          invoice.status === 'confirmed' ? 'bg-[#DBEAFE] text-[#1E40AF]' :
                          invoice.status === 'partially_paid' ? 'bg-[#FEF3C7] text-[#92400E]' :
                          invoice.status === 'cancelled' ? 'bg-[#FEE2E2] text-[#991B1B]' :
                          'bg-[#F5F5F5] text-[#6b7280]'
                        }`}>
                          {invoice.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap items-center">
                          {invoice.status === 'draft' && (
                            <button onClick={() => handleConfirm(invoice)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#4A7AE0] text-[#4A7AE0] text-xs font-medium" data-testid={`confirm-${invoice.id}`}>
                              Confirm
                            </button>
                          )}
                          {(invoice.status === 'confirmed' || invoice.status === 'partially_paid') && parseFloat(invoice.balance_due) > 0 && (
                            <button onClick={() => handleRecordPayment(invoice)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#4A7AE0] text-[#4A7AE0] text-xs font-medium" data-testid={`payment-${invoice.id}`}>
                              Record Payment
                            </button>
                          )}
                          {invoice.status !== 'cancelled' && invoice.status !== 'draft' && (
                            <button onClick={() => handleCancel(invoice)} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#EF4444] text-[#EF4444] text-xs font-medium" data-testid={`cancel-${invoice.id}`}>
                              Cancel
                            </button>
                          )}
                          {invoice.status !== 'draft' && invoice.status !== 'cancelled' && (
                            <button
                              onClick={() => window.open(`/sales/invoices/${invoice.id}/print`, '_blank')}
                              className="inline-flex items-center gap-1 text-[#4A7AE0] hover:bg-[#EFF6FF] border border-[#4A7AE0] rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                              data-testid={`print-${invoice.id}`}
                              title="Print Invoice"
                            >
                              <Printer className="w-3 h-3" />
                              Print
                            </button>
                          )}
                          {invoice.status === 'draft' && (
                            <button
                              onClick={() => handleDelete(invoice)}
                              className="inline-flex items-center gap-1 text-[#EF4444] hover:bg-[#FEE2E2] border border-[#EF4444] rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                              data-testid={`delete-${invoice.id}`}
                              title="Delete Invoice"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && <InvoiceFormSimple onClose={() => setShowForm(false)} onSuccess={handleFormSuccess} />}
      {showPaymentModal && selectedInvoice && (
        <PaymentModal invoice={selectedInvoice} onClose={() => setShowPaymentModal(false)} onSuccess={handlePaymentSuccess} />
      )}
    </div>
  );
};

export default Invoices;
